import React, { useContext, useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { kebabCase } from 'lodash'

import mergeQueryParams from 'marketing-site/lib/mergeQueryParams'
import { setTeammateAppUrl } from 'marketing-site/lib/teammateAppUrl'
import CTALinkOverridePropsContext from 'marketing-site/src/components/context/CTALinkOverridePropsContext'
import SignupCTAParamsContext from 'marketing-site/src/components/context/SignupCTAParamsContext'
import queryString from 'query-string'
import { CTATheme, Icon, renderIconSVG } from '../../utils'
import { Text } from '../Text'
import { IProps } from './index'
import styles from './styles.scss'
import useClickHandler from './hooks/useClickHandler'

export const CTALink: React.FC<IProps> = (props) => {
  const overrideProps = useContext(CTALinkOverridePropsContext)
  const {
    url,
    text: label,
    arrow = true,
    bgColor,
    newWindow = false,
    small,
    eventContext,
    eventObjectName,
    dataAttribute,
    wide,
    textSize,
    openMessenger,
    preloadedMessengerText,
    forwardQueryParams,
    customBotId,
    surveyId,
    anchorRef: refFromProps,
    children,
    icon,
    onClick,
  } = { ...props, ...overrideProps }

  const [href, setHref] = useState<string>(url)
  const additionalParams = useContext(SignupCTAParamsContext)

  useEffect(() => {
    let newUrl = setTeammateAppUrl(url)

    if (forwardQueryParams && location.search) {
      const currentQueryParams = queryString.parse(location.search)
      newUrl = mergeQueryParams(newUrl, currentQueryParams)
    }

    newUrl = mergeQueryParams(newUrl, additionalParams)

    setHref(newUrl)
  }, [url, additionalParams, forwardQueryParams, href, setHref])

  // We get a ref as a prop in certain cases. If we don't, let's create our own ref here.
  const ref = useRef<HTMLAnchorElement>(null)
  const anchorRef = refFromProps || ref

  const { handleClick, isLoading } = useClickHandler({
    anchorRef,
    label,
    onClick,
    openMessenger,
    preloadedMessengerText,
    dataAttribute,
    surveyId,
    customBotId,
    eventContext,
    eventObjectName,
  })

  const dataAttributeObject = dataAttribute ? { [dataAttribute]: 'true' } : {}

  const isValidChildren = React.isValidElement(children)

  const renderIcon = () => {
    if (arrow || icon === Icon.Arrow) return renderIconSVG(Icon.Arrow, 'cta-link__arrow')
    if (icon) return renderIconSVG(icon, 'cta-link__icon')
    return null
  }

  const CTAThemeNames: Record<CTATheme, string> = {
    [CTATheme.BlackFill]: 'black-fill',
    [CTATheme.BlackOutline]: 'black-outline',
    [CTATheme.WhiteOutline]: 'white-outline',
    [CTATheme.LinkOnlyBlack]: 'link-only-black',
    [CTATheme.LinkOnlyWhite]: 'link-only-white',
    [CTATheme.TealFill]: 'teal-fill',
    [CTATheme.BlackFillTransparentHover]: 'black-fill-transparent-hover',
    [CTATheme.BlueFill]: 'blue-fill',
    [CTATheme.BlueOutline]: 'blue-outline',
    [CTATheme.LinkOnlyBlue]: 'link-only-blue',
  }

  return (
    <a
      ref={anchorRef}
      className={classnames(`cta-link cta-link--${CTAThemeNames[bgColor]}`, {
        'cta-link--small': small,
        'cta-link--wide': wide,
        'cta-link--loading': isLoading,
        'cta-link--unstyled': isValidChildren,
      })}
      data-testid={`cta-link-${kebabCase(label)}`}
      href={href}
      target={newWindow ? '_blank' : ''}
      rel={newWindow ? 'noopener' : ''}
      onClick={handleClick}
      {...dataAttributeObject}
    >
      {isValidChildren ? (
        <>{children}</>
      ) : (
        <div className="button-content">
          <Text brand="default" size={textSize || 'button-bold'}>
            {label}
          </Text>
          {renderIcon()}
        </div>
      )}
      <style jsx>{styles}</style>
    </a>
  )
}
